// What a rate actually costs, in each currency it will be collected in
// (SM-503).
//
// A percentage on its own is not reviewable — 7% of what, paid in what?
// The Fees pages have always answered the first half with a worked
// example, and answered the second half in dollars for every market on
// the platform. That was true while Gold was the only currency a fill
// could move; a Game buy is charged in Red and a Game sell in Green, and
// a dollar sign in front of either is the wrong unit next to a real
// number.
//
// So the example is drawn once per currency, with that currency's
// configured symbol, decimals and name — the same presentation the
// wallets and the revenue tables use. The lines sit side by side and are
// never summed: they are the SAME fee seen in different books, not
// several fees that add up to anything.
import { useCurrencySettings } from "@/components/currency/CurrencyPresentationProvider";
import {
  formatCurrencyText,
  resolveCurrencyPresentation,
  type CurrencyCode,
} from "@/currency";
import { cn } from "@/lib/cn";

/** The example fill every preview on these pages quotes. */
export const EXAMPLE_TICKETS = 100;
/**
 * …at even odds, where the curved fee formula peaks: half of one whole
 * unit, in whatever currency the book trades.
 *
 * Carried as a fraction of `unitMinor` rather than the literal "0.50" it
 * used to be, so the price is rendered by the same formatter as the fee
 * and picks up the currency's configured decimals. A symbol glued to a
 * bare number — the old `$0.50` — reads as "GOLD0.50" the moment a
 * currency's mark is a word rather than a sign.
 */
export const EXAMPLE_PRICE_FRACTION = 0.5;

/**
 * The fee that example fill pays, in wire minor units.
 *
 * Fees follow the curved formula `qty * rate * p * (1 - p)`, so at
 * p = 0.5 a 100-ticket fill pays `100 * rate * 0.25` = 25 * rate whole
 * units — and one whole unit is `unitMinor` on the wire, in every
 * currency (SM-484).
 *
 * Rounded to an integer before it leaves this function: `formatSpark`
 * truncates whatever it is handed through an integer path, so a float
 * arriving there loses its sub-unit digits silently.
 */
export function exampleFeeMinor(
  rateMinor: string,
  precision: number,
  unitMinor: number,
): number | null {
  const rate = Number(rateMinor) / Math.pow(10, precision);
  if (!Number.isFinite(rate)) return null;
  return Math.round(EXAMPLE_TICKETS * rate * 0.25 * unitMinor);
}

/**
 * One line per currency: what the example fill pays if it happens in
 * that book.
 *
 * A zero rate gets one sentence instead of three identical zeros — the
 * fact that nothing is charged does not vary by currency.
 */
export function FeeExampleLines({
  rate,
  precision,
  codes,
  className,
}: {
  /** The rate in wire minor units, scaled by `precision`. */
  rate: string;
  precision: number;
  /** The currencies this rate will be collected in. */
  codes: readonly CurrencyCode[];
  className?: string;
}) {
  const settings = useCurrencySettings();
  if (!rate || rate === "0") {
    return (
      <p className={cn("text-2xs leading-relaxed text-t-4", className)}>
        No fee is charged at this rate.
      </p>
    );
  }
  if (codes.length === 0) return null;
  return (
    <ul className={cn("space-y-0.5", className)}>
      {codes.map((code) => {
        const presentation = resolveCurrencyPresentation(code, settings);
        const minor = exampleFeeMinor(rate, precision, presentation.unitMinor);
        if (minor === null) return null;
        return (
          <li key={code} className="text-2xs leading-relaxed text-t-4">
            {`100 tickets filled at ${formatCurrencyText(
              presentation,
              String(Math.round(presentation.unitMinor * EXAMPLE_PRICE_FRACTION)),
            )} pay about ${formatCurrencyText(presentation, String(minor))}.`}
          </li>
        );
      })}
    </ul>
  );
}
